import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Plus, CreditCard, Calendar, Settings, Edit3, Trash2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Dog, Tag, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetsContext';
import { useTransactionTypes } from '../contexts/TransactionTypesContext';
import TransactionTypesModal from '../components/TransactionTypesModal';
import ConfirmationModal from '../components/ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import Portal from '../components/Portal';
import { useTransactionsAggregate } from '../utils/useTransactionsAggregate';
import { updateTransactionsAggregationOnCreate, updateTransactionsAggregationOnUpdate, updateTransactionsAggregationOnDelete } from '../utils/transactionsAggregations';

const Transactions = () => {
  const { pets, loading: petsLoading } = usePets();
  const { transactionTypes, loading: transactionTypesLoading } = useTransactionTypes();
  const [showModal, setShowModal] = useState(false);
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 12;
  const [formData, setFormData] = useState({
    tipo: 'receita',
    idPet: '',
    amount: '',
    idTipoTransacao: '',
    description: '',
    date: ''
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    year: '',
    month: '',
    idTipoTransacao: '',
    idPet: '',
    sortOrder: 'newest' // 'newest' ou 'oldest'
  });

  const { user } = useAuth();
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();
  
  // Usar agregados de transações (atualização em tempo real)
  const { transactions: allTransactions, availableYears: yearsFromTransactions } = useTransactionsAggregate();

  // Função para obter data da transação
  const getTransactionDate = (transaction) => {
    const dateField = transaction.data || transaction.date;
    if (!dateField) return null;
    try {
      if (dateField instanceof Date) {
        return dateField;
      }
      if (dateField?.toDate) {
        return dateField.toDate();
      }
      return new Date(dateField);
    } catch (error) {
      return null;
    }
  };

  // Aplicar filtros e ordenação
  const filteredAndSortedTransactions = (allTransactions || [])
    .filter(transaction => {
      // Filtro por ano
      if (filters.year) {
        const date = getTransactionDate(transaction);
        if (!date || isNaN(date.getTime())) return false;
        if (date.getFullYear().toString() !== filters.year) return false;
      }

      // Filtro por mês
      if (filters.month) {
        const date = getTransactionDate(transaction);
        if (!date || isNaN(date.getTime())) return false;
        if ((date.getMonth() + 1).toString() !== filters.month) return false;
      }

      // Filtro por tipo de transação
      if (filters.idTipoTransacao) {
        // Se o filtro for "agendamento-avulso", verificar nomeTipoTransacao
        if (filters.idTipoTransacao === 'agendamento-avulso') {
          if (transaction.nomeTipoTransacao !== 'Agen. avulso') return false;
        } else {
          // Para outros tipos, verificar idTipoTransacao
          if (transaction.idTipoTransacao !== filters.idTipoTransacao) return false;
        }
      }

      // Filtro por pet
      if (filters.idPet) {
        if (transaction.idPet !== filters.idPet) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = getTransactionDate(a);
      const dateB = getTransactionDate(b);
      
      // Se uma data é inválida, coloca no final
      if (!dateA || isNaN(dateA.getTime())) return 1;
      if (!dateB || isNaN(dateB.getTime())) return -1;
      
      if (filters.sortOrder === 'newest') {
        return dateB.getTime() - dateA.getTime(); // Mais recente primeiro
      } else {
        return dateA.getTime() - dateB.getTime(); // Mais antigo primeiro
      }
    });

  // Paginação
  const totalPages = Math.ceil(filteredAndSortedTransactions.length / transactionsPerPage);
  const startIndex = (currentPage - 1) * transactionsPerPage;
  const endIndex = startIndex + transactionsPerPage;
  const paginatedTransactions = filteredAndSortedTransactions.slice(startIndex, endIndex);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Funções de navegação de página
  const loadNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const loadPrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Resetar página quando os dados ou filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [allTransactions, filters]);
  
  // Usar anos disponíveis dos agregados mensais de transações
  const [availableYears, setAvailableYears] = useState([]);
  
  useEffect(() => {
    if (yearsFromTransactions && yearsFromTransactions.length > 0) {
      setAvailableYears(yearsFromTransactions);
      
      // Se não há filtro de ano definido, definir o primeiro ano disponível
      if (!filters.year) {
        setFilters(prev => ({ ...prev, year: yearsFromTransactions[0].toString() }));
      } else {
        // Verificar se o ano do filtro está na lista, se não, usar o primeiro disponível
        const currentYearFilter = parseInt(filters.year);
        if (!yearsFromTransactions.includes(currentYearFilter)) {
          setFilters(prev => ({ ...prev, year: yearsFromTransactions[0].toString() }));
        }
      }
    } else {
      setAvailableYears([]);
      setFilters(prev => ({ ...prev, year: '' }));
    }
  }, [yearsFromTransactions]);
  
  // Obter anos disponíveis dos agregados
  const getUniqueYears = () => {
    return availableYears.map(year => year.toString()).sort((a, b) => parseInt(b) - parseInt(a));
  };

  const getUniqueMonths = () => {
    const months = [
      { value: '1', label: 'Janeiro' },
      { value: '2', label: 'Fevereiro' },
      { value: '3', label: 'Março' },
      { value: '4', label: 'Abril' },
      { value: '5', label: 'Maio' },
      { value: '6', label: 'Junho' },
      { value: '7', label: 'Julho' },
      { value: '8', label: 'Agosto' },
      { value: '9', label: 'Setembro' },
      { value: '10', label: 'Outubro' },
      { value: '11', label: 'Novembro' },
      { value: '12', label: 'Dezembro' }
    ];
    return months;
  };

  // Calcular totais: soma bruta - se é receita soma em receita, se é despesa soma em despesa
  const totalRevenue = filteredAndSortedTransactions
    .filter(t => t.tipo === 'receita')
    .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);

  const totalExpenses = filteredAndSortedTransactions
    .filter(t => t.tipo === 'despesa')
    .reduce((sum, t) => sum + parseFloat(t.valor || t.amount || 0), 0);

  // Nota: Os contextos (PetsContext e TransactionTypesContext) já carregam os dados automaticamente
  // Não precisamos de um useEffect aqui para evitar leituras extras do Firebase

  // Limpar tipo de transação quando o tipo (revenue/expense) mudar (apenas se não estiver editando)
  useEffect(() => {
    if (!editingTransaction) {
      setFormData(prev => ({ 
        ...prev, 
        idPet: formData.tipo === 'despesa' ? '' : prev.idPet,
        idTipoTransacao: '', 
        description: '', 
        date: '' 
      }));
    }
  }, [formData.tipo, editingTransaction]);

  // Travar scroll do fundo quando o modal Nova/Editar Transação estiver aberto
  useEffect(() => {
    if (showModal) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Converter data do formato YYYY-MM-DD para Date usando horário local
      let transactionDate;
      if (formData.date) {
        // Parse da data como horário local (YYYY-MM-DD)
        const [year, month, day] = formData.date.split('-').map(Number);
        transactionDate = new Date(year, month - 1, day);
      } else if (editingTransaction) {
        const editingDateField = editingTransaction.data || editingTransaction.date;
        transactionDate = editingDateField instanceof Date 
          ? editingDateField 
          : (editingDateField?.toDate ? editingDateField.toDate() : new Date(editingDateField));
      } else {
        transactionDate = new Date();
      }

      const transactionData = {
        tipo: formData.tipo,
        idPet: formData.tipo === 'despesa' ? null : (formData.idPet || null),
        valor: parseFloat(formData.amount),
        idTipoTransacao: formData.idTipoTransacao,
        descricao: formData.description.trim() || '',
        data: transactionDate
      };


      if (editingTransaction) {
        // Preparar dados antigos para agregação
        const oldTransactionData = {
          ...editingTransaction,
          data: editingTransaction.data || (editingTransaction.date instanceof Date ? Timestamp.fromDate(editingTransaction.date) : editingTransaction.date)
        };
        
        // Preparar dados novos para agregação
        const newTransactionData = {
          id: editingTransaction.id,
          tipo: transactionData.tipo,
          idPet: transactionData.idPet,
          valor: transactionData.valor,
          idTipoTransacao: transactionData.idTipoTransacao,
          descricao: transactionData.descricao,
          data: transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data,
          idAgendamento: transactionData.idAgendamento || null
        };
        
        // Editar transação existente - converter data para Timestamp antes de salvar
        const transactionDataWithTimestamp = {
          tipo: transactionData.tipo,
          idPet: transactionData.idPet,
          valor: transactionData.valor,
          idTipoTransacao: transactionData.idTipoTransacao,
          descricao: transactionData.descricao,
          data: transactionData.data ? (transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data) : Timestamp.fromDate(new Date()),
          atualizadoEm: Timestamp.now()
        };
        await updateDoc(doc(db, 'transacoes', editingTransaction.id), transactionDataWithTimestamp);
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        // Buscar nomePet e nomeTipoTransacao para incluir no agregado
        const nomePet = formData.tipo === 'despesa' ? null : (pets.find(p => p.id === formData.idPet)?.nome || null);
        const nomeTipoTransacao = transactionTypes.find(t => t.id === formData.idTipoTransacao)?.nome || null;
        
        const updatedTransactionData = { 
          id: editingTransaction.id,
          tipo: transactionData.tipo,
          idPet: transactionData.idPet,
          valor: transactionData.valor,
          idTipoTransacao: transactionData.idTipoTransacao,
          descricao: transactionData.descricao,
          data: transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data,
          idAgendamento: editingTransaction.idAgendamento || null,
          atualizadoEm: Timestamp.now(),
          criadoEm: editingTransaction.criadoEm || Timestamp.now(),
          criadoPor: editingTransaction.criadoPor || null,
          nomePet: nomePet,
          nomeTipoTransacao: nomeTipoTransacao
        };
        
        // Atualizar agregado de transações (aguardar conclusão)
        try {
          await updateTransactionsAggregationOnUpdate(editingTransaction, updatedTransactionData);
        } catch (err) {
          // Erro ao atualizar agregado de transações
          // Erro ao atualizar agregado ao editar transação
        }
        
        // O listener em tempo real atualizará automaticamente
      } else {
        // Criar nova transação - mapear explicitamente apenas campos traduzidos
        const transactionDataWithTimestamp = {
          tipo: transactionData.tipo,
          idPet: transactionData.idPet,
          valor: transactionData.valor,
          idTipoTransacao: transactionData.idTipoTransacao,
          descricao: transactionData.descricao,
          data: transactionData.data ? (transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data) : Timestamp.fromDate(new Date()),
          criadoEm: Timestamp.now(),
          criadoPor: user ? {
            uid: user.uid,
            nomeExibicao: user.nomeExibicao || 'Usuário'
          } : null
        };
        
        const docRef = await addDoc(collection(db, 'transacoes'), transactionDataWithTimestamp);
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        // Buscar nomePet e nomeTipoTransacao para incluir no agregado
        const nomePet = formData.tipo === 'despesa' ? null : (pets.find(p => p.id === formData.idPet)?.nome || null);
        const nomeTipoTransacao = transactionTypes.find(t => t.id === formData.idTipoTransacao)?.nome || null;
        
        const newTransactionData = { 
          id: docRef.id,
          tipo: transactionData.tipo,
          idPet: transactionData.idPet,
          valor: transactionData.valor,
          idTipoTransacao: transactionData.idTipoTransacao,
          descricao: transactionData.descricao,
          data: transactionData.data instanceof Date ? Timestamp.fromDate(transactionData.data) : transactionData.data,
          idAgendamento: transactionData.idAgendamento || null,
          criadoEm: Timestamp.now(),
          criadoPor: user ? {
            uid: user.uid,
            nomeExibicao: user.nomeExibicao || 'Usuário'
          } : null,
          nomePet: nomePet,
          nomeTipoTransacao: nomeTipoTransacao
        };
        
        // Atualizar agregado de transações (aguardar conclusão)
        try {
          await updateTransactionsAggregationOnCreate(newTransactionData);
        } catch (err) {
          // Erro ao atualizar agregado de transações
          // Erro ao atualizar agregado ao criar transação
        }
        
        // O listener em tempo real atualizará automaticamente
      }
      
      setShowModal(false);
      setEditingTransaction(null);
      setFormData({
        tipo: 'receita',
        idPet: '',
        amount: '',
        idTipoTransacao: '',
        description: '',
        date: ''
      });
      // Dados serão atualizados automaticamente via onSnapshot
    } catch (error) {
      // Erro ao salvar transação
    }
  };

  const getPetName = (transaction) => {
    return transaction.nomePet || '—';
  };

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string' || str === '—') return str || '—';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getTransactionTypeName = (transaction) => {
    const typeName = transaction.nomeTipoTransacao;
    if (!typeName) return '—';
    return capitalizeFirst(typeName);
  };

  // Função para determinar se uma transação é futura e seu status
  const getTransactionStatus = (transaction) => {
    const today = new Date();
    const transactionDateField = transaction.data || transaction.date;
    const transactionDate = transactionDateField instanceof Date ? transactionDateField : (transactionDateField?.toDate ? transactionDateField.toDate() : new Date(transactionDateField));
    
    // Resetar horas para comparar apenas as datas
    today.setHours(0, 0, 0, 0);
    transactionDate.setHours(0, 0, 0, 0);
    
    const isFuture = transactionDate > today;
    
    if (isFuture) {
      return transaction.tipo === 'receita' ? 'a receber' : 'a pagar';
    }
    
    return null; // Transação passada ou presente
  };


  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    const transactionDateObj = transaction.data || transaction.date;
    const transactionDate = transactionDateObj instanceof Date ? transactionDateObj : (transactionDateObj?.toDate ? transactionDateObj.toDate() : new Date(transactionDateObj));
    const formattedDate = transactionDate.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    setFormData({
      tipo: transaction.tipo,
      idPet: transaction.idPet || '',
      amount: (transaction.valor || transaction.amount || 0).toString(),
      idTipoTransacao: transaction.idTipoTransacao || '',
      description: transaction.descricao || transaction.description || '',
      date: formattedDate
    });
    setShowModal(true);
  };

  const handleDelete = async (transaction) => {
    const message = `Tem certeza que deseja excluir esta transação de ${transaction.tipo === 'receita' ? 'receita' : 'despesa'} no valor de R$ ${(transaction.valor || transaction.amount || 0).toLocaleString('pt-BR')}? Esta ação não pode ser desfeita.`;
    
    const confirmed = await showConfirmation({
      title: 'Excluir Transação',
      message: message,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'transacoes', transaction.id));
      
      // Atualizar agregado de transações (aguardar conclusão)
      try {
        await updateTransactionsAggregationOnDelete(transaction.id, transaction.data || transaction.date);
      } catch (err) {
        // Erro ao atualizar agregado de transações
      }
      
      // O listener em tempo real atualizará automaticamente
    } catch (error) {
      // Erro ao excluir
    }
  };





  // Lucro líquido: faturamento - custos totais
  const lucroLiquido = totalRevenue - totalExpenses;


  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
            <CreditCard className="w-6 h-6" />
            <span>Transações</span>
          </h1>
          <p className="hidden sm:block text-muted-light dark:text-muted-dark">Gerencie receitas e despesas</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTypesModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Settings className="w-5 h-5" />
            <span>Tipos de Transação</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Nova Transação</span>
            <span className="sm:hidden">Novo</span>
          </button>
        </div>
      </div>

      {/* Resumo Financeiro */}
      <div className="card py-2 mb-1">
        <div className="grid grid-cols-3 gap-0">
          <div className="flex flex-col items-center justify-center text-center border-r border-border-light dark:border-border-dark">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-1">Faturamento</p>
            <p className="text-2xl font-bold text-success-600">
              R$ {totalRevenue.toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center text-center border-r border-border-light dark:border-border-dark">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-1">Custos</p>
            <p className="text-2xl font-bold text-danger-600">
              R$ {totalExpenses.toLocaleString('pt-BR')}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-base font-medium text-muted-light dark:text-muted-dark mb-0">Lucro Líquido</p>
            <p className="text-xs text-muted-light dark:text-muted-dark mb-0" style={{ fontporte: '0.65rem' }}>faturamento - custos</p>
            <p className={`text-2xl font-bold ${lucroLiquido >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
              R$ {lucroLiquido.toLocaleString('pt-BR')}
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Filtro de Ano */}
          <div>
            <label className="block text-sm font-medium text-foreground-light dark:text-foreground-dark mb-1">
              Ano
            </label>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
              <select
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                className="input-field w-fit min-w-[7rem]"
              >
                {getUniqueYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtro de Mês */}
          <div>
            <label className="block text-sm font-medium text-foreground-light dark:text-foreground-dark mb-1">
              Mês
            </label>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
              <select
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                className="input-field w-fit min-w-[7rem]"
              >
                <option value="">Todos os meses</option>
                {getUniqueMonths().map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtro de Tipo de Transação */}
          <div>
            <label className="block text-sm font-medium text-foreground-light dark:text-foreground-dark mb-1">
              Tipo
            </label>
            <div className="flex items-center space-x-2">
              <Tag className="w-4 h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
              <select
                value={filters.idTipoTransacao}
                onChange={(e) => setFilters({ ...filters, idTipoTransacao: e.target.value })}
                className="input-field w-fit min-w-[7rem]"
              >
                <option value="">Todos os tipos</option>
                <option value="agendamento-avulso">Agendamento Avulso</option>
                {transactionTypes
                  .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                  .map((type) => (
                    <option key={type.id} value={type.id}>
                      {capitalizeFirst(type.nome)}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Filtro de Pet */}
          <div>
            <label className="block text-sm font-medium text-foreground-light dark:text-foreground-dark mb-1">
              Pet
            </label>
            <div className="flex items-center space-x-2">
              <Dog className="w-4 h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
              <select
                value={filters.idPet}
                onChange={(e) => setFilters({ ...filters, idPet: e.target.value })}
                className="input-field w-fit min-w-[7rem]"
              >
                <option value="">Todos os pets</option>
                {pets
                  .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                  .map((pet) => (
                    <option key={pet.id} value={pet.id}>
                      {pet.nome} ({pet.tutor || 'Tutor não informado'})
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Ordenação */}
          <div>
            <label className="block text-sm font-medium text-foreground-light dark:text-foreground-dark mb-1">
              Ordenar
            </label>
            <div className="flex items-center space-x-2">
              <ArrowUpDown className="w-4 h-4 text-muted-light dark:text-muted-dark flex-shrink-0" />
              <button
                type="button"
                onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'newest' ? 'oldest' : 'newest' })}
                className="input-field w-fit flex items-center justify-start"
              >
                <span>{filters.sortOrder === 'newest' ? 'Mais Atual' : 'Mais Antigo'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Botão Limpar Filtros */}
        {(filters.month || filters.idTipoTransacao || filters.idPet) && (
          <div className="mb-4">
            <button
              onClick={() => setFilters({
                year: availableYears.length > 0 ? availableYears[0].toString() : '',
                month: '',
                idTipoTransacao: '',
                idPet: '',
                sortOrder: 'newest'
              })}
              className="btn-secondary text-sm"
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Lista de Transações - DADOS BRUTOS DO AGREGADO */}
      <div className="card mt-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground-light dark:text-foreground-dark">
            Lista de Transações
          </h2>
        </div>
        <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border-light dark:divide-border-dark">
                  <thead className="bg-secondary-light dark:bg-secondary-dark">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Tipo de Transação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Pet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Usuário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-light dark:text-muted-dark uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card-light dark:bg-card-dark divide-y divide-border-light dark:divide-border-dark">
                    {paginatedTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-muted-light dark:text-muted-dark">
                          Nenhuma transação encontrada.
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        const rows = paginatedTransactions.map((transaction, index) => {
                          try {
                            return (
                <tr key={transaction.id} className="hover:bg-secondary-light dark:hover:bg-secondary-dark">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground-light dark:text-foreground-dark">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-muted-light dark:text-muted-dark" />
                      <div className="flex flex-col">
                        <span>
                          {(() => {
                            try {
                              if (!transaction.date) return '—';
                              const dateObj = transaction.date instanceof Date 
                                ? transaction.date 
                                : (transaction.date?.toDate ? transaction.date.toDate() : new Date(transaction.date));
                              if (isNaN(dateObj.getTime())) return '—';
                              return format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
                            } catch (error) {
                              return '—';
                            }
                          })()}
                        </span>
                        {getTransactionStatus(transaction) && (
                          <span className={`text-xs font-medium ${
                            getTransactionStatus(transaction) === 'a receber' 
                              ? 'text-yellow-600 dark:text-yellow-400' 
                              : 'text-orange-600 dark:text-orange-400'
                          }`}>
                            {getTransactionStatus(transaction)}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground-light dark:text-foreground-dark">
                    {getTransactionTypeName(transaction)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground-light dark:text-foreground-dark">
                    {(() => {
                      const nomePet = getPetName(transaction);
                      if (nomePet === '—') return '—';
                      
                      // Se houver idPet, criar link para o pet
                      if (transaction.idPet) {
                        return (
                          <Link
                            to={`/pets/${transaction.idPet}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-secondary-light dark:bg-secondary-dark text-foreground-light dark:text-foreground-dark hover:bg-secondary-light/80 transition-colors"
                            title="Ver detalhes do pet"
                          >
                            {nomePet}
                          </Link>
                        );
                      }
                      
                      // Se não houver idPet mas houver nomePet (agendamento avulso), mostrar apenas o nome
                      return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-secondary-light dark:bg-secondary-dark text-foreground-light dark:text-foreground-dark">
                          {nomePet}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground-light dark:text-foreground-dark">
                    <div>
                      <span>
                        {transaction.descricao || transaction.description || '—'}
                      </span>
                      {transaction.maintenanceDescription && (
                        <div className="mt-1 text-xs text-muted-light dark:text-muted-dark">
                          {transaction.maintenanceDescription}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground-light dark:text-foreground-dark">
                    {transaction.criadoPor?.nomeExibicao || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.tipo === 'receita' 
                          ? 'bg-success-100 text-success-800 dark:bg-success-800 dark:text-success-100' 
                          : 'bg-danger-100 text-danger-800 dark:bg-danger-800 dark:text-danger-100'
                      }`}
                    >
                      {transaction.tipo === 'receita' ? '+' : '-'} R$ {(transaction.valor || transaction.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground-light dark:text-foreground-dark">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEdit(transaction)}
                        className="p-1 text-muted-light dark:text-muted-dark hover:text-primary-600 transition-colors"
                        title="Editar transação"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(transaction)}
                        className="p-1 text-muted-light dark:text-muted-dark hover:text-danger-600 transition-colors"
                        title="Excluir transação"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                            );
                          } catch (error) {
                            return (
                              <tr key={transaction.id}>
                                <td colSpan="7" className="px-6 py-4 text-center text-danger-600">
                                  Erro ao renderizar transação {transaction.id}: {error.message}
                              </td>
                            </tr>
                            );
                          }
                        });
                        return rows;
                      })()
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de Paginação */}
        {filteredAndSortedTransactions.length > transactionsPerPage && (
          <div className="flex items-center justify-center mt-6 pt-4 border-t border-border-light dark:border-border-dark">
            <div className="flex items-center space-x-2">
              <button
                onClick={loadPrevPage}
                disabled={!hasPrevPage}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hasPrevPage
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Anterior</span>
              </button>
              
              <span className="text-sm text-muted-light dark:text-muted-dark">
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={loadNextPage}
                disabled={!hasNextPage}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hasNextPage
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span>Próxima</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Nova Transação */}
      {showModal && (
        <Portal>
          <div 
            className="modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowModal(false);
                setEditingTransaction(null);
                setFormData({
                  tipo: 'receita',
                  idPet: '',
                  amount: '',
                  idTipoTransacao: '',
                  description: '',
                  date: ''
                });
              }
            }}
          >
            <div className="max-w-2xl w-full overflow-hidden rounded-lg bg-card-light dark:bg-card-dark">
              <div className="flex items-center justify-between gap-2 border-b border-border-light px-6 py-4 dark:border-border-dark">
                <h2 className="min-w-0 text-xl font-bold text-foreground-light dark:text-foreground-dark">
                  {editingTransaction ? 'Editar Transação' : 'Nova Transação'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTransaction(null);
                    setFormData({
                      tipo: 'receita',
                      idPet: '',
                      amount: '',
                      idTipoTransacao: '',
                      description: '',
                      date: ''
                    });
                  }}
                  className="shrink-0 p-1 text-muted-light transition-colors hover:text-foreground-light dark:text-muted-dark dark:hover:text-foreground-dark"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Tipo</label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className="input-field"
                    >
                      <option value="receita">Receita</option>
                      <option value="despesa">Despesa</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Tipo de Transação</label>
                    <select
                      value={formData.idTipoTransacao}
                      onChange={(e) => setFormData({ ...formData, idTipoTransacao: e.target.value })}
                      className="input-field"
                      required
                    >
                      <option value="">Selecione um tipo</option>
                      {transactionTypes
                        .filter(type => {
                          const categoriaTipo = type.categoria;
                          // Mapear valores em inglês para português para compatibilidade
                          const tipoTransacao = formData.tipo;
                          return categoriaTipo === tipoTransacao;
                        })
                        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                        .map((type) => (
                          <option key={type.id} value={type.id}>
                            {capitalizeFirst(type.nome)}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Valor (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="input-field"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Data</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>

                </div>

                <div>
                  <label className="label">Pet</label>
                  <select
                    value={formData.idPet}
                    onChange={(e) => setFormData({ ...formData, idPet: e.target.value })}
                    className="input-field"
                    disabled={formData.tipo === 'despesa'}
                  >
                    <option value="">
                      {formData.tipo === 'despesa' ? 'Não aplicável para despesas' : 'Selecione um pet'}
                    </option>
                    {pets
                      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                      .map((pet) => (
                        <option key={pet.id} value={pet.id}>
                          {pet.nome} ({pet.tutor || 'Tutor não informado'})
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="label">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field"
                    placeholder="Descreva a transação (opcional)..."
                    rows={3}
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingTransaction(null);
                      setFormData({
                        tipo: 'receita',
                        idPet: '',
                        amount: '',
                        idTipoTransacao: '',
                        description: '',
                        date: ''
                      });
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                  >
                    {editingTransaction ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Tipos de Transação */}
      <TransactionTypesModal 
        isOpen={showTypesModal} 
        onClose={() => {
          setShowTypesModal(false);
          // Dados serão atualizados automaticamente via listener em tempo real
        }} 
      />

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />

      {/* Modal de Confirmação de Pagamento */}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </div>
  );
};

export default Transactions;
